import React, { useState, useEffect } from "react";
import { Heart, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getPostLikes } from "../../../services/postService";
import { useAuth } from "../../../context/AuthContext";
import FetchErrorState from "../../Common/FetchErrorState";
import LikesSkeleton from "../../Loaders/LikesSkeleton";

const styles = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .animate-slide-up {
    animation: slide-up 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("likes-modal-styles")
) {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  styleSheet.id = "likes-modal-styles";
  document.head.appendChild(styleSheet);
}

// 1. Helper function to get initials from username or name
const getInitials = (name) => {
  if (!name) return "U";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

// 2. Helper function to get profile picture
const getProfilePicture = (profilePic) => {
  if (profilePic && typeof profilePic === "object") {
    return profilePic.thumbnail || profilePic.small || profilePic.medium;
  }
  return typeof profilePic === "string" ? profilePic : null;
};

// 3. Like Item Component - moved outside main component
const LikeItem = ({ like, onUserClick }) => {
  const profilePictureUrl = getProfilePicture(like.likedBy?.profilePicture);
  const initials = getInitials(like.likedBy?.name);

  return (
    <div
      onClick={() => onUserClick(like.likedBy?.username)}
      className="flex items-center gap-3 px-1 sm:px-0 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      {/* Profile Picture */}
      <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-700">
        {profilePictureUrl ? (
          <img
            src={profilePictureUrl}
            alt={like.likedBy?.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600">
            <span className="text-slate-600 dark:text-slate-300 font-semibold text-sm">
              {initials}
            </span>
          </div>
        )}
      </div>

      {/* Username */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate">
          {like.likedBy?.username}
        </p>
      </div>

      {/* Heart Icon */}
      <Heart className="w-4 h-4 text-red-500 fill-red-500 flex-shrink-0" />
    </div>
  );
};

const LikesModal = ({ postId, onClose, initialLikesCount = 0 }) => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Handle navigation to user profile
  const handleUserClick = (username) => {
    onClose();
    navigate(`/${username}`);
  };

  // Fetch initial likes
  const fetchLikes = async () => {
    try {
      setLoading(true);
      const result = await getPostLikes(apiCall, postId, 10, null);

      setLikes(result.likes || []);
      setHasMore(result.hasMore || false);
      setNextCursor(result.nextCursor || null);
      setError(null);
    } catch (err) {
      setError("We couldn't load the likes for this post.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLikes();
  }, [postId, apiCall]);

  // Load more likes
  const loadMore = async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const result = await getPostLikes(apiCall, postId, 10, nextCursor);

      setLikes((prev) => [...prev, ...(result.likes || [])]);
      setHasMore(result.hasMore || false);
      setNextCursor(result.nextCursor || null);
    } catch (err) {
      // Silent — the list already shown stays intact; scrolling again retries.
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle scroll for infinite loading
  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && hasMore && !loadingMore) {
      loadMore();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-3xl max-h-[85vh] flex flex-col animate-slide-up px-2 sm:px-0 sm:max-w-xl sm:mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center p-2 sm:p-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <h3 className="text-base sm:text-lg font-semibold text-black dark:text-white">
            Likes
          </h3>
        </div>

        {/* Likes List - Scrollable Area */}
        <div
          className="h-[40vh] overflow-y-auto p-3 sm:p-4 space-y-0"
          onScroll={handleScroll}
        >
          {loading ? (
            // ✅ Using LikesSkeleton instead of loading spinner
            <LikesSkeleton count={5} />
          ) : error ? (
            <FetchErrorState
              message={error}
              onRetry={fetchLikes}
            />
          ) : likes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <Heart className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm sm:text-sm">No likes yet</p>
              <p className="text-xs sm:text-xs opacity-70">
                Be the first to like this post
              </p>
            </div>
          ) : (
            <>
              {likes.map((like) => (
                <LikeItem
                  key={like._id}
                  like={like}
                  onUserClick={handleUserClick}
                />
              ))}

              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}

              {/* No More Likes */}
              {!hasMore && likes.length > 0 && (
                <div className="font-semibold text-center py-3 text-xs text-gray-400 dark:text-gray-500">
                  No more likes
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LikesModal;